/**
 * License Validation Service
 *
 * Handles communication with the Cloudflare Worker for license key validation.
 * This service respects the privacy-first architecture - it NEVER handles user workflow data.
 */

// Types matching the Cloudflare Worker API
export interface LicenseValidationRequest {
  licenseKey: string;
}

export interface LicenseValidationResponse {
  valid: boolean;
  error?: string;
  meta?: {
    productName: string;
    customerName: string;
    customerEmail: string;
    expiresAt: string | null;
  };
}

export class LicenseValidationError extends Error {
  constructor(
    message: string,
    public code: 'NETWORK_ERROR' | 'INVALID_RESPONSE' | 'VALIDATION_FAILED'
  ) {
    super(message);
    this.name = 'LicenseValidationError';
  }
}

/**
 * Get the Cloudflare Worker URL from environment variables
 */
function getWorkerUrl(): string {
  const url = import.meta.env.VITE_WORKER_URL;

  if (!url) {
    throw new LicenseValidationError(
      'Worker URL not configured. Please set VITE_WORKER_URL in .env.local',
      'INVALID_RESPONSE'
    );
  }

  return url;
}

/**
 * Validate a license key with the Cloudflare Worker
 *
 * @param licenseKey - The license key to validate
 * @returns Promise<LicenseValidationResponse>
 * @throws LicenseValidationError if validation fails
 */
export async function validateLicenseKey(
  licenseKey: string
): Promise<LicenseValidationResponse> {
  // Basic client-side validation
  const trimmedKey = licenseKey.trim();

  if (!trimmedKey) {
    throw new LicenseValidationError(
      'License key cannot be empty',
      'VALIDATION_FAILED'
    );
  }

  if (trimmedKey.length < 10 || trimmedKey.length > 500) {
    throw new LicenseValidationError(
      'License key format is invalid',
      'VALIDATION_FAILED'
    );
  }

  const workerUrl = getWorkerUrl();
  const endpoint = `${workerUrl}/validate`;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        licenseKey: trimmedKey,
      } as LicenseValidationRequest),
    });

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new LicenseValidationError(
        'Invalid response from license validation service',
        'INVALID_RESPONSE'
      );
    }

    const data = await response.json();

    // Validate response structure
    if (typeof data.valid !== 'boolean') {
      throw new LicenseValidationError(
        'Invalid response format from license validation service',
        'INVALID_RESPONSE'
      );
    }

    return data as LicenseValidationResponse;
  } catch (error) {
    // Handle LicenseValidationError directly
    if (error instanceof LicenseValidationError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof TypeError) {
      throw new LicenseValidationError(
        'Network error. Please check your connection and ensure the worker is running.',
        'NETWORK_ERROR'
      );
    }

    // Handle other errors
    throw new LicenseValidationError(
      'Failed to validate license. Please try again later.',
      'NETWORK_ERROR'
    );
  }
}

/**
 * Check if a license is stored in localStorage
 * This is used to persist license state across page refreshes
 */
export function getStoredLicense(): string | null {
  try {
    return localStorage.getItem('zaplens_license_key');
  } catch {
    return null;
  }
}

/**
 * Store a validated license key in localStorage
 */
export function storeLicense(licenseKey: string): void {
  try {
    localStorage.setItem('zaplens_license_key', licenseKey);
  } catch (error) {
    console.warn('Failed to store license in localStorage:', error);
  }
}

/**
 * Remove stored license from localStorage
 */
export function clearStoredLicense(): void {
  try {
    localStorage.removeItem('zaplens_license_key');
  } catch (error) {
    console.warn('Failed to clear license from localStorage:', error);
  }
}

/**
 * Validate stored license on app initialization
 * Returns true if a valid stored license exists
 */
export async function validateStoredLicense(): Promise<boolean> {
  const storedKey = getStoredLicense();

  if (!storedKey) {
    return false;
  }

  try {
    const result = await validateLicenseKey(storedKey);

    if (!result.valid) {
      // License is no longer valid, clear it
      clearStoredLicense();
      return false;
    }

    return true;
  } catch {
    // If validation fails, clear the stored license
    clearStoredLicense();
    return false;
  }
}
